import {useCallback, useState} from "react";
import {useDropzone, FileRejection} from "react-dropzone";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton";
import {CloudArrowUpIcon, XMarkIcon} from "@heroicons/react/24/outline";
import type {DocumentDirection} from "../../../../../generated/enums.ts";

interface DocumentDropzoneProps {
    label: string;
    documentDirection: DocumentDirection;
    supportedFileTypes: Record<string, string[]>;
    supportedFileExtensions: string[];
    maxFiles: number;
    maxSize: number;
    onUpload?: (files: File[], documentDirection: DocumentDirection) => Promise<void>;
    files?: File[];
    setFiles: (files: File[]) => void;
    fileTypesLabel?: string;
}

const iconBadgeClasses: Record<DocumentDirection, string> = {
    purchase_invoice: 'bg-sky-50 text-sky-500',
    sales_invoice: 'bg-emerald-50 text-emerald-500',
    other: 'bg-gray-100 text-gray-500',
};

type RejectionReason = 'too-many-files' | 'file-too-large' | 'file-invalid-type' | 'other';

const MAX_LISTED_FILE_NAMES = 10;

function formatSizeLimit(bytes: number): string {
    return `${Math.round(bytes / (1024 * 1024))} MB`;
}

function listFileNames(names: string[]): string {
    const listed = names.slice(0, MAX_LISTED_FILE_NAMES).join(', ');
    return names.length > MAX_LISTED_FILE_NAMES ? `${listed} en ${names.length - MAX_LISTED_FILE_NAMES} meer` : listed;
}

const rejectionReasonOrder: RejectionReason[] = ['too-many-files', 'file-too-large', 'file-invalid-type', 'other'];

function rejectionReasonOf(rejection: FileRejection): RejectionReason {
    const code = rejection.errors[0]?.code;
    return code === 'too-many-files' || code === 'file-too-large' || code === 'file-invalid-type' ? code : 'other';
}

function describeRejections(
    fileRejections: FileRejection[],
    supportedFileExtensions: string[],
    maxFiles: number,
    maxSize: number,
): string[] {
    const namesByReason = new Map<RejectionReason, string[]>();
    fileRejections.forEach((rejection) => {
        const reason = rejectionReasonOf(rejection);
        namesByReason.set(reason, [...(namesByReason.get(reason) ?? []), rejection.file.name]);
    });

    const messageFor: Record<RejectionReason, (names: string[]) => string> = {
        'too-many-files': () => `Te veel bestanden geselecteerd. Je kunt maximaal ${maxFiles} bestanden tegelijk uploaden.`,
        'file-too-large': (names) => `Te groot (maximaal ${formatSizeLimit(maxSize)} per bestand): ${listFileNames(names)}`,
        'file-invalid-type': (names) => `Bestandstype niet ondersteund: ${listFileNames(names)}. Alleen bestandstypen ${supportedFileExtensions.join(', ')} worden ondersteund.`,
        'other': (names) => `Niet toegevoegd: ${listFileNames(names)}`,
    };

    return rejectionReasonOrder
        .filter((reason) => namesByReason.has(reason))
        .map((reason) => messageFor[reason](namesByReason.get(reason) ?? []));
}

export function DocumentDropzone({
    label,
    documentDirection,
    supportedFileTypes,
    supportedFileExtensions,
    maxFiles,
    maxSize,
    onUpload,
    files = [],
    setFiles,
    fileTypesLabel,
}: DocumentDropzoneProps) {
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);
    const [filesAreBeingUploaded, setFilesAreBeingUploaded] = useState<boolean>(false);

    const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
        setUploadErrors(describeRejections(fileRejections, supportedFileExtensions, maxFiles, maxSize));
        if (acceptedFiles.length > 0) setFiles(acceptedFiles);
    }, [setFiles, supportedFileExtensions, maxFiles, maxSize]);

    const dropzone = useDropzone({
        onDrop,
        maxFiles,
        maxSize,
        accept: supportedFileTypes,
    });

    async function handleUpload() {
        setFilesAreBeingUploaded(true);
        await onUpload?.(files, documentDirection);
        setFilesAreBeingUploaded(false);
        setFiles([]);
    }

    return (
        <div>
            <div {...dropzone.getRootProps({role: 'button', 'aria-label': label, className: `flex items-center gap-6 w-full rounded-2xl border-2 border-dashed px-8 py-7 cursor-pointer transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${dropzone.isDragActive ? 'border-primary bg-primarysurface' : 'border-gray-200 bg-white hover:border-primary hover:bg-primarysurface'}`})}>
                <input {...dropzone.getInputProps()} />
                <div className={`size-14 shrink-0 rounded-2xl grid place-items-center ${iconBadgeClasses[documentDirection]}`}>
                    <CloudArrowUpIcon className="size-7" aria-hidden="true"/>
                </div>
                <div className="min-w-0">
                    <div className="text-lg font-semibold text-gray-900">{label}</div>
                    {dropzone.isDragActive ? (
                        <div className="text-primary font-medium">Loslaten om te uploaden</div>
                    ) : (
                        <div className="text-gray-500">Sleep bestanden hierheen of <span className="font-medium text-primary">kies bestanden</span></div>
                    )}
                    <div className="mt-1 text-xs text-gray-400">
                        {fileTypesLabel ?? supportedFileExtensions.join(', ').toUpperCase()} · max. {formatSizeLimit(maxSize)} per bestand · tot {maxFiles} tegelijk
                    </div>
                </div>
            </div>
            {uploadErrors.length > 0 && <div role="alert"
                className="border border-red-300 bg-red-100 rounded-lg p-4 my-2 flex items-start justify-between gap-2">
                <ul className="space-y-1">
                    {uploadErrors.map((message) => <li key={message}>{message}</li>)}
                </ul>
                <button type="button" aria-label="Sluiten" onClick={() => setUploadErrors([])}>
                    <XMarkIcon className="h-6 w-6 shrink-0 text-red-400"/>
                </button>
            </div>}
            {files.length > 0 && (
                <div className="file-list">
                    <ul>
                        {files.map((file) => (
                            <li key={file.name}
                                className="border border-gray-300 rounded-lg p-4 my-2 flex items-center justify-between cursor-pointer">
                                <div><span>{file.name}</span> <span
                                    className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</span>
                                </div>
                                <div className="h-8 w-8 hover:bg-gray-100 rounded-full p-1">
                                    <XMarkIcon onClick={() => {
                                        setFiles(files.filter(f => f.name !== file.name))
                                    }}/>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <PrimaryButton
                        title={filesAreBeingUploaded ? 'Uploaden...' : 'Uploaden'}
                        onClick={handleUpload}
                        disabled={filesAreBeingUploaded}
                    />
                </div>
            )}
        </div>
    );
} 